import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Compass, 
  LocateFixed, 
  Search, 
  ChevronDown, 
  X, 
  Target, 
  ArrowRight,
  Sprout
} from 'lucide-react';
import { useSearch } from '@/src/contexts/SearchContext';
import { FeatureType } from '@/src/types';
import DebouncedInput from '@/src/components/DebouncedInput';
import SuggestionDropdown from '@/src/components/SuggestionDropdown';
import GrassrootsIdeatorModal from '@/src/components/GrassrootsIdeatorModal';

interface DiscoveryHubProps {
  discLocRef: React.RefObject<HTMLInputElement | null>;
  filteredCountries: string[];
}

const DiscoveryHub: React.FC<DiscoveryHubProps> = ({
  discLocRef,
  filteredCountries
}) => {
  const { 
    state, 
    dispatch, 
    handleRunScan, 
    handleLocInputChange, 
    handleDensityInputChange, 
    handleSuggestionSelect 
  } = useSearch();

  const countrySearchRef = useRef<HTMLInputElement>(null);
  const [isGrassrootsModalOpen, setIsGrassrootsModalOpen] = useState(false);

  const handleGrassrootsVentureToLaunchpad = (idea: any) => {
    setIsGrassrootsModalOpen(false);
    dispatch({
      type: 'SET_STATE',
      payload: {
        launchpadLocation: idea.location || state.discoveryLocation || state.discoveryLocality || 'Kampala, Uganda',
        launchpadSector: idea.sector || idea.title || 'Local Enterprise',
        activeFeature: FeatureType.LocaleLaunchpad
      }
    });

    dispatch({
      type: 'SET_RESULT',
      payload: {
        result: {
          summary: `Grassroots Observational Blueprint for "${idea.title}": ${idea.description}`,
          ideas: [idea]
        },
        activeFeature: FeatureType.LocaleLaunchpad
      }
    });
  };

  return (
    <div className="w-full max-w-[800px] mx-auto bg-[#3a3430] border border-white/[0.06] rounded-xl relative overflow-hidden h-full min-h-[580px] flex flex-col text-left">
      <div className="p-6 sm:p-10 flex-1 relative z-20 w-full overflow-hidden flex flex-col">
        {/* Header Section */}
        <div className="space-y-4 mb-6 shrink-0">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-[#F26522] flex items-center justify-center text-white font-bold text-sm shrink-0">
                D
              </div>
              <h3 className="text-xl font-medium text-white tracking-tight truncate">Discovery Hub</h3>
            </div>
            <p className="text-xs text-white/60 leading-relaxed font-normal max-w-md">
              Explore any area. Uncover what neighborhoods are actually known for. View business density heatmaps. Verify real competitors with live data.
            </p>
          </div>

          {/* Grassroots Ideator Feature Banner Button */}
          <div>
            <button
              type="button"
              onClick={() => setIsGrassrootsModalOpen(true)}
              className="w-full bg-[#322D29] border border-white/[0.08] hover:border-[#F26522]/50 text-white p-3 rounded-lg transition-all flex items-center justify-between cursor-pointer text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded bg-[#F26522] text-[#FFFFFF] flex items-center justify-center shrink-0">
                  <Sprout className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-[11px] font-medium text-white block">
                    eNvesta AI Ideator
                  </span>
                  <span className="text-[10px] text-white/50 block">
                    No search engine data? Describe local surroundings to map grounded ventures.
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-[10px] font-medium text-[#F26522] shrink-0 ml-2">
                <span className="hidden sm:inline">Open Ideator</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="space-y-5 flex-1 flex flex-col min-h-0">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-white/70 block">Neighborhood Identity Focus</label>
              <div className="relative w-full">
                <DebouncedInput 
                  ref={discLocRef as any} 
                  placeholder="What is this neighborhood known for? (e.g. Shoreditch, Nyarugenge)..." 
                  className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] rounded-lg text-sm text-white focus:border-[#F26522] outline-none transition-all pr-12 placeholder:text-xs placeholder:text-white/40" 
                  value={state.discoveryLocation} 
                  onChange={val => handleLocInputChange(val, 'discovery')}
                  onKeyDown={(e: any) => e.key === 'Enter' && !state.densitySectorWarning && handleRunScan(FeatureType.ReverseDiscovery)}
                />
                <button 
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch({ type: 'SET_MAP_PICKER', payload: { isOpen: true, target: 'discovery' } }); }} 
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded flex items-center justify-center text-white/40 hover:text-[#F26522] hover:bg-white/5 transition-colors cursor-pointer"
                  title="Select location on map"
                >
                  <LocateFixed className="w-4 h-4" />
                </button>
                <SuggestionDropdown 
                  hub="discovery" 
                  isFetchingSuggestions={state.isFetchingSuggestions}
                  locSuggestions={state.activeSuggestionHub === 'discovery' ? state.locSuggestions : []}
                  onSelect={(name) => handleSuggestionSelect(name, 'discovery')}
                  onClose={() => dispatch({ type: 'SET_SUGGESTIONS', payload: [] })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium text-white/70 block">Comparative Density Search</label>
              <div className="relative group space-y-2">
                <DebouncedInput 
                  placeholder="Where is this common? (e.g. specialty coffee, fintech)..." 
                  className="w-full px-4 py-3 bg-[#322D29] border border-white/[0.08] rounded-lg text-sm text-white outline-none transition-all focus:border-[#F26522] placeholder:text-xs placeholder:text-white/40" 
                  value={state.densitySector} 
                  onChange={val => handleDensityInputChange(val)} 
                  onKeyDown={(e: any) => e.key === 'Enter' && !state.densitySectorWarning && handleRunScan(FeatureType.ReverseDiscovery)}
                />
                {state.densitySectorWarning && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-[#322D29] border border-[#F26522] rounded-lg">
                    <Target className="w-3.5 h-3.5 text-[#F26522]" />
                    <span className="text-xs text-[#F26522] leading-tight">
                      Alert: Geographic input detected in focus field.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Unified Geographic Context Row */}
          <div className="flex flex-col sm:flex-row gap-4 sm:items-end w-full">
            {/* Country Picker */}
            <div className="w-full sm:w-[170px] shrink-0 space-y-1.5">
              <label className="text-xs font-medium text-white/70 block">Country Node</label>
              <div className="relative">
                <button 
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch({ type: 'SET_MENU_OPEN', payload: { menu: 'country', isOpen: !state.isCountryMenuOpen } }); }}
                  className={`w-full h-[46px] flex items-center justify-between px-3.5 border rounded-lg transition-all uppercase tracking-wider cursor-pointer ${state.isCountryMenuOpen ? 'bg-[#322D29] border-[#F26522] text-[#FFFFFF]' : 'bg-[#322D29] border-white/[0.08] text-white hover:border-white/20'}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-base shrink-0 leading-none">{state.discoveryCountry.split(' ')[0]}</span>
                    <span className="text-xs font-medium truncate leading-none mt-0.5">{state.discoveryCountry.split(' ').slice(1).join(' ')}</span>
                  </div>
                  <ChevronDown className={`w-3 h-3 shrink-0 transition-transform duration-300 ${state.isCountryMenuOpen ? 'rotate-180 text-white' : 'text-white/40'}`} />
                </button>

                <AnimatePresence>
                  {state.isCountryMenuOpen && (
                    <motion.div 
                      key="country-dropdown-menu"
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute left-0 bottom-full mb-2 w-[280px] sm:w-[320px] bg-[#322D29] border border-white/[0.1] shadow-2xl rounded-xl z-[100] flex flex-col overflow-hidden origin-bottom-left"
                    >
                      <header className="px-4 py-3 border-b border-white/[0.06] bg-[#2a2522]">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium uppercase tracking-wider text-white/60">Network Nodes</span>
                          <button 
                            onClick={() => dispatch({ type: 'SET_MENU_OPEN', payload: { menu: 'country', isOpen: false } })}
                            className="text-white/40 hover:text-[#F26522] transition-colors cursor-pointer"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                          <DebouncedInput 
                            ref={countrySearchRef}
                            autoFocus
                            placeholder="Filter nodes..." 
                            className="w-full pl-8 pr-3 py-1.5 bg-[#3a3430] border border-white/[0.08] rounded-lg text-xs outline-none focus:border-[#F26522] text-white placeholder:text-white/40 transition-all font-mono"
                            value={state.countrySearch}
                            onChange={val => dispatch({ type: 'SET_SEARCH_TERM', payload: { type: 'country', term: val } })}
                          />
                        </div>
                      </header>
                      <div className="max-h-[240px] overflow-y-auto custom-scrollbar p-1">
                        {filteredCountries.map((c, idx) => (
                          <button 
                            key={`country-${c}-${idx}`} 
                            onClick={() => { dispatch({ type: 'SET_STATE', payload: { discoveryCountry: c } }); dispatch({ type: 'SET_MENU_OPEN', payload: { menu: 'country', isOpen: false } }); }}
                            className={`w-full text-left px-3 py-2 hover:bg-[#3a3430] rounded-lg text-xs font-normal transition-all flex items-center justify-between cursor-pointer ${state.discoveryCountry === c ? 'bg-[#F26522]/10 text-[#F26522]' : 'text-white'}`}
                          >
                            <div className="flex items-center gap-2.5">
                              <span className="text-base shrink-0">{c.split(' ')[0]}</span>
                              <span>{c.split(' ').slice(1).join(' ')}</span>
                            </div>
                            {state.discoveryCountry === c && <div className="w-1.5 h-1.5 rounded-full bg-[#F26522]"></div>}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Locality Refinement Input */}
            <div className="flex-1 space-y-1.5 min-w-0">
              <label className="text-xs font-medium text-white/70 block">Locality Refinement</label>
              <div className="relative w-full">
                <DebouncedInput 
                  placeholder="Specific city or region..." 
                  className="w-full px-4 h-[46px] bg-[#322D29] border border-white/[0.08] rounded-lg text-sm outline-none transition-all focus:border-[#F26522] text-white placeholder:text-xs placeholder:text-white/40"
                  value={state.discoveryLocality}
                  onChange={val => handleLocInputChange(val, 'locality')}
                />
                <SuggestionDropdown 
                  hub="locality" 
                  isFetchingSuggestions={state.isFetchingSuggestions}
                  locSuggestions={state.activeSuggestionHub === 'locality' ? state.locSuggestions : []}
                  onSelect={(name) => handleSuggestionSelect(name, 'locality')}
                  onClose={() => dispatch({ type: 'SET_SUGGESTIONS', payload: [] })}
                />
              </div>
            </div>
          </div>

          <div className="mt-auto pt-4">
            <button 
              onClick={() => handleRunScan(state.densitySector ? FeatureType.DensityScout : FeatureType.ReverseDiscovery)} 
              disabled={(!state.discoveryLocation && !state.densitySector) || state.loading || state.densitySectorWarning} 
              className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-[#FFFFFF] py-3 rounded-lg font-medium text-xs uppercase tracking-wider transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-2 relative z-20 cursor-pointer"
            >
              {state.loading && (state.activeFeature === FeatureType.DensityScout || state.activeFeature === FeatureType.ReverseDiscovery) ? (
                <>
                  <div className="w-3.5 h-3.5 rounded-full border-2 border-white/80 border-t-transparent animate-spin" />
                  <span>Sweeping Radar...</span>
                </>
              ) : (
                <>
                  <span>{state.densitySector ? 'Map Density' : 'Reveal Identity'}</span>
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Grassroots Ideator Modal */}
      <GrassrootsIdeatorModal
        isOpen={isGrassrootsModalOpen}
        onClose={() => setIsGrassrootsModalOpen(false)}
        initialLocation={state.discoveryLocation || state.discoveryLocality}
        preferredCurrency={state.preferredCurrency}
        onSelectVentureForLaunchpad={handleGrassrootsVentureToLaunchpad}
      />
    </div>
  );
};

export default DiscoveryHub;
